import React from 'react';
import { SOFT, SOFT_BORDER, FOCUS_RING, normaliseTone, cx } from './toneStyles';

/**
 * Card — the app's base surface (wraps the `.card` CSS class, which keeps p-4;
 * padding='sm'|'none' override it here so callers never need `!p-*`).
 *
 * Props
 *   padding   'md' (16px, default) | 'sm' (12px) | 'none'
 *   tone      'default' | 'info' | 'warm' | 'success' | 'warning' | 'danger'
 *             | 'feed' | 'sleep' | 'nappy' | 'activity' | 'pump' | 'health'
 *   interactive  hover shadow + press scale; with onClick and a non-button `as`
 *                it becomes keyboard-operable (role="button", Enter/Space)
 *   elevated  raised (shadow-md, dark ring)
 *   as        element/component to render (default 'div')
 *
 * Callers never need `!` overrides — pass padding/tone instead of className hacks.
 */
// Utility classes beat the @layer components `.card` p-4, so no `!` is needed.
const PAD = { md: '', sm: 'p-3', none: 'p-0' };

export default function Card({
  children,
  padding = 'md',
  tone = 'default',
  interactive = false,
  elevated = false,
  as: Tag = 'div',
  className = '',
  onClick,
  onKeyDown,
  role,
  tabIndex,
  ...props
}) {
  const t = normaliseTone(tone);
  const toned = t !== 'neutral';
  const isButtonLike = interactive && typeof onClick === 'function' && Tag !== 'button' && Tag !== 'a';

  const handleKeyDown = (e) => {
    if (onKeyDown) onKeyDown(e);
    if (!isButtonLike || e.defaultPrevented) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <Tag
      className={cx(
        'card',
        PAD[padding] || '',
        toned && SOFT[t],
        toned && SOFT_BORDER[t],
        elevated && 'shadow-md dark:ring-1 dark:ring-white/5',
        interactive &&
          cx(
            'cursor-pointer text-left hover:shadow-md active:scale-[0.98] transition-[box-shadow,transform] duration-base ease-out',
            FOCUS_RING
          ),
        className
      )}
      onClick={onClick}
      onKeyDown={isButtonLike || onKeyDown ? handleKeyDown : undefined}
      role={isButtonLike ? 'button' : role}
      tabIndex={isButtonLike ? 0 : tabIndex}
      {...props}
    >
      {children}
    </Tag>
  );
}
